import { ANALYTICS_EVENTS, safeAnalyticsPath, safeProps, publicAnalyticsHost, sourceGroup, botLabel, type AnalyticsName, type AnalyticsEvent } from "./analytics-catalog";
import { getConsent } from "./consent";

let allowed = false;
let initializing: Promise<boolean> | undefined;
let session: string | undefined;
let interacted = false;
let source: AnalyticsEvent["source"] = "direct";
let pending: AnalyticsEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let lastPage: string | undefined;

export async function initializeAnalytics(): Promise<boolean> {
  if (initializing) return initializing;
  initializing = (async () => {
    if (typeof window === "undefined" || !publicAnalyticsHost(window.location.hostname)) return false;
    if (new URLSearchParams(window.location.search).has("debug") || new URLSearchParams(window.location.search).has("test")) return false;
    // Storage only detects the possible presence of a login. The server decides
    // eligibility; unresolved/invalid logins fail closed, never assume non-admin.
    try {
      const key = Object.keys(localStorage).find(k => /^sb-.*-auth-token$/.test(k));
      if (key) {
        const stored = JSON.parse(localStorage.getItem(key) ?? "{}");
        if (stored.access_token) {
          const res = await fetch("/api/public/track-event", { headers: { Authorization: `Bearer ${stored.access_token}` } });
          if (!res.ok || (await res.json()).excluded !== false) return false;
        } else return false;
      }
    } catch { initializing = undefined; return false; }
    allowed = true;
    session = crypto.randomUUID();
    source = sourceGroup(window.location.search, document.referrer, window.location.hostname);
    return true;
  })();
  return initializing;
}
export function analyticsAllowed() {
  if (typeof window === "undefined") return false;
  if (/^\/(?:admin|auth|login)(?:\/|$)/.test(window.location.pathname)) allowed = false;
  return allowed && safeAnalyticsPath(window.location.pathname) !== null;
}
export function markInteraction() { interacted = true; }
export function googleEvent(event: string, raw: Record<string, unknown> = {}) {
  if (["field_interaction", "form_error", "form_abandon", "form_start", "contact_open"].includes(event)) return;
  if (!analyticsAllowed() || getConsent() !== "granted" || !document.getElementById("gtm-script")) return;
  const w = window as Window & { dataLayer?: unknown[] };
  w.dataLayer ??= [];
  const { field: _field, form_id: _formId, category: _category, ...props } = safeProps(raw);
  w.dataLayer.push({ event, ...props, page_path: window.location.pathname,
    page_location: `${window.location.origin}${window.location.pathname}` });
}
export function trackAnalytics(event: AnalyticsName, raw: Record<string, unknown> = {}) {
  if (!analyticsAllowed() || !session) return;
  const page = safeAnalyticsPath(window.location.pathname);
  if (!page) return;
  const lang = window.location.pathname.split("/")[1];
  pending.push({ id: crypto.randomUUID(), occurred_at: new Date().toISOString(), event, page,
    lang: lang === "fr" || lang === "en" ? lang : "nl", session_id: session, source,
    device: window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
    is_bot: botLabel(navigator.userAgent, navigator.webdriver), interacted, props: safeProps(raw) });
  if (pending.length >= 20) flushAnalytics();
  else if (!timer) timer = setTimeout(flushAnalytics, 750);
}
export function recordEvent(event: string, raw: Record<string, unknown> = {}) {
  if (event !== "page_view" && event !== "contact_open") interacted = true;
  if ((ANALYTICS_EVENTS as readonly string[]).includes(event)) trackAnalytics(event as AnalyticsName, raw);
  googleEvent(event, raw);
}
export function recordPage(path: string) {
  if (!analyticsAllowed() || path === lastPage) return;
  lastPage = path;
  recordEvent("page_view");
}
export function flushAnalytics() {
  if (timer) clearTimeout(timer);
  timer = undefined;
  if (!pending.length) return;
  const events = pending.splice(0, 20);
  const body = JSON.stringify({ version: 2, events });
  const blob = new Blob([body], { type: "application/json" });
  if (!navigator.sendBeacon?.("/api/public/track-event", blob)) {
    void fetch("/api/public/track-event", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  }
  if (pending.length) flushAnalytics();
}